import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ProfileNavbar } from "../components/profile/ProfileNavbar";
import { SiteFooter } from "../components/SiteFooter";
import { ContactSection } from "../components/ContactSection";
import { getProjectBySlug, projectsData } from "../data/projectsData";
import { ImageWithFallback } from "../components/ImageWithFallback";
import { motion } from "motion/react";
import { SEO } from "../components/SEO";

const PROJECT_FOOTER_LINKS = [
  { label: "All projects", to: "/olga#projects" },
  { label: "How to hire me", to: "/olga#engagement" },
  { label: "The studio", to: "/" },
  { label: "Contact", section: "contact" },
];

const MORE_WORK_COUNT = 3;

function pickRandom<T>(items: T[], count: number): T[] {
  const pool = [...items];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

export function ProjectPage() {
  const { slug } = useParams<{ slug: string }>();
  const project = getProjectBySlug(slug || "");

  const candidates = useMemo(
    () => projectsData.filter(p => p.slug !== slug),
    [slug]
  );

  // The server render and the first hydration pass have to match, so start from a
  // stable slice and reshuffle on the client once mounted (and on every slug change).
  const [otherProjects, setOtherProjects] = useState(() =>
    candidates.slice(0, MORE_WORK_COUNT)
  );

  useEffect(() => {
    setOtherProjects(pickRandom(candidates, MORE_WORK_COUNT));
  }, [candidates]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold mb-4 font-['Outfit',sans-serif]">Project not found</h1>
        <Link to="/olga#projects" className="text-blue-600 hover:underline">Back to all projects</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#e7f1f1] min-h-screen">
      <SEO 
        title={project.title} 
        description={project.description} 
        image={project.heroImage}
        url={`https://www.geekcow.com/projects/${project.slug}`}
        projectTitle={project.title}
      />
      <ProfileNavbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col gap-6"
        >
          <div className="flex flex-col gap-2">
            <span className="font-['Space_Mono',monospace] text-sm tracking-widest text-[#666] uppercase">
              {project.principalrole}
            </span>
            <h1 className="font-['Outfit',sans-serif] font-black text-5xl md:text-7xl lg:text-8xl text-[#141414] leading-tight">
              {project.title}
            </h1>
          </div>
          <p className="font-['Outfit',sans-serif] text-xl md:text-2xl text-[#666] max-w-2xl">
            {project.subtitle}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-12 md:rounded-2xl overflow-hidden aspect-video shadow-2xl"
        >
          <ImageWithFallback 
            src={project.heroImage} 
            alt={project.title} 
            className="w-full h-full object-cover"
          />
        </motion.div>
      </section>

      {/* Project Details Grid */}
      <section className="py-16 px-4 md:px-8 lg:px-16 max-w-7xl mx-auto border-t border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          
          {/* Metadata Column */}
          <div className="md:col-span-1">
            <div className="sticky top-32 flex flex-col gap-8">
              <div>
                <h3 className="font-['Space_Mono',monospace] text-xs font-bold uppercase text-[#999] mb-2">Role</h3>
                <ul className="flex flex-col gap-1">
                  {project.role.map((role, i) => (
                    <li key={i} className="font-['Outfit',sans-serif] font-medium text-[#141414]">{role}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="font-['Space_Mono',monospace] text-xs font-bold uppercase text-[#999] mb-2">Scope</h3>
                <p className="font-['Outfit',sans-serif] font-medium text-[#141414]">{project.scope}</p>
              </div>
              
              <div>
                <h3 className="font-['Space_Mono',monospace] text-xs font-bold uppercase text-[#999] mb-2">Direction</h3>
                <a 
                  href={project.direction.startsWith('http') ? project.direction : '#'} 
                  target={project.direction.startsWith('http') ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                  className="font-['Outfit',sans-serif] font-medium text-[#256af4] hover:underline break-all"
                >
                  {project.direction}
                </a>
              </div>
            </div>
          </div>

          {/* Description & Process Column */}
          <div className="md:col-span-3 flex flex-col gap-12">
            <div>
              <h2 className="font-['Outfit',sans-serif] font-bold text-3xl mb-6">Overview</h2>
              <p className="font-['Public_Sans',sans-serif] text-lg text-[#444] leading-relaxed max-w-3xl">
                {project.description}
              </p>
            </div>

            {project.process.length > 0 && (
              <div>
                 <h2 className="font-['Outfit',sans-serif] font-bold text-3xl mb-6">The Process</h2>
                 <div className="grid grid-cols-1 gap-8">
                   {project.process.map((step, i) => (
                     <div key={i} className="bg-[#f9f9f9] p-6 rounded-xl">
                       <h4 className="font-['Outfit',sans-serif] font-bold text-xl mb-2 text-[#141414]">
                         0{i + 1}. {step.title}
                       </h4>
                       <p className="font-['Public_Sans',sans-serif] text-[#666] leading-relaxed">
                         {step.description.split("\n").map((line, lineIndex, lines) => {
                           const renderBold = (text: string) => {
                             const arrowRegex = /[^\s]+(?:\s?→\s?[^\s]+)+/g;
                             const parts: Array<string | JSX.Element> = [];
                             let lastIndex = 0;
                             let match;

                             while ((match = arrowRegex.exec(text)) !== null) {
                               if (match.index > lastIndex) {
                                 parts.push(text.slice(lastIndex, match.index));
                               }
                               parts.push(
                                 <strong key={`bold-${i}-${lineIndex}-${match.index}`}>{match[0]}</strong>
                               );
                               lastIndex = match.index + match[0].length;
                             }

                             if (lastIndex < text.length) {
                               parts.push(text.slice(lastIndex));
                             }

                             return parts;
                           };

                           const renderLine = (text: string) => {
                             if (text.includes("**")) {
                               return text.split("**").map((segment, segmentIndex) =>
                                 segmentIndex % 2 === 1 ? (
                                   <strong key={`bold-${i}-${lineIndex}-${segmentIndex}`}>{segment}</strong>
                                 ) : (
                                   segment
                                 )
                               );
                             }

                             return renderBold(text);
                           };

                           return (
                             <span key={`${i}-${lineIndex}`}>
                               {renderLine(line)}
                               {lineIndex < lines.length - 1 && <br />}
                             </span>
                           );
                         })}
                       </p>
                     </div>
                   ))}
                 </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
        <h2 className="font-['Outfit',sans-serif] font-bold text-3xl mb-8">Process Gallery</h2>
        <div className="flex flex-col gap-8">
          {project.gallery.slice(1).map((item, idx) => (
            <div key={idx} className="w-full">
              <div className="md:rounded-2xl overflow-hidden shadow-lg mb-3">
                 <ImageWithFallback 
                   src={item.url} 
                   alt={item.alt} 
                   className="w-full h-auto object-cover"
                 />
              </div>
              {item.caption && (
                <p className="text-center font-['Public_Sans',sans-serif] text-sm text-[#888] italic">
                  {item.caption}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Other Projects Gallery */}
      <section className="py-24 bg-[#e7f1f1] px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Outfit',sans-serif] font-bold text-3xl mb-12 text-center">More Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherProjects.map((other) => (
              <Link 
                key={other.id} 
                to={`/projects/${other.slug}`}
                className="group flex flex-col gap-4 cursor-pointer"
              >
                <div className="md:rounded-xl overflow-hidden aspect-[4/3] shadow-md group-hover:shadow-xl transition-all duration-300 relative">
                   <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors z-10" />
                   <ImageWithFallback 
                     src={other.heroImage} 
                     alt={other.title} 
                     className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                   />
                </div>
                <div>
                  <h3 className="font-['Outfit',sans-serif] font-bold text-xl text-[#141414] group-hover:text-blue-600 transition-colors">
                    {other.title}
                  </h3>
                  <p className="font-['Space_Mono',monospace] text-xs uppercase tracking-widest text-[#666] mt-1">
                    {other.principalrole}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div id="contact">
        <ContactSection showFooter={false} />
      </div>

      <SiteFooter
        face="profile"
        blurb="Olga Molina — product designer and full-stack developer, specialised in security and compliance. Freelance and interim, from the Netherlands."
        navLinks={PROJECT_FOOTER_LINKS}
        onSectionClick={(id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />
    </div>
  );
}
